import { MAINNET } from '@/constants/env';
import { Wallet, WALLET_LIST } from '@/constants/wallets';
import { useAptosWallet } from '@/hooks/aptos/aptos-wallet';
import { useWallet } from '@/hooks/wallet';
import { walletAtom, WalletType } from '@/state/wallet';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from '../../hooks/support/translate';
import { Button } from '../atom/Button';
import { Image } from '../atom/Image';
import { Loader } from '../atom/Loader';

interface WalletModalProps {
    onConnected: () => void;
}

export const WalletOptions: FC<WalletModalProps> = (props) => {
    const walletState = useRecoilValue(walletAtom);
    const { connectWallet } = useWallet();
    const { aptosConnectWallet } = useAptosWallet();
    const { t } = useTranslation();
    const router = useRouter();
    const isAptosPage = router.asPath.includes('/aptos');

    const isWalletConnectActive = (id: WalletType) => walletState.walletType === id && id === 'wallet_connect';

    const handleClick = async (wallet: Wallet) => {
        if (!wallet.isAvailable()) {
            window.open(wallet.downloadUrl);
            return;
        }
        isAptosPage ? await aptosConnectWallet(wallet._id) : await connectWallet(wallet._id);
        props.onConnected();
    };

    const walletList = isAptosPage
        ? WALLET_LIST.filter((wallet) => wallet._id === 'aptos')
        : WALLET_LIST.filter((wallet) => wallet._id !== 'aptos');

    return (
        <>
            <div className="mt-4 mb-12 text-left t__body--b3 md:mb-6 text-grey-20">
                {t('modal.wallet.description.p1')}&nbsp;
                <a href="https://claystack.com/terms" className="text-main-40">
                    {t('modal.wallet.description.p2')}
                </a>
                {/*&nbsp;&amp; {t('modal.wallet.description.p3')}*/}
            </div>
            <div className="space-y-2">
                {walletList.map(
                    (wallet, index) =>
                        (!MAINNET || wallet.inMainnet) && (
                            <button
                                id={wallet._id}
                                key={index}
                                aria-label={wallet.name}
                                onClick={() => handleClick(wallet)}
                                className="flex items-center w-full gap-2 p-4 text-white rounded-lg t__button--u1 bg-grey-50 disabled:bg-opacity-50 ring-1 ring-transparent hover:ring-main-60"
                            >
                                <span>{wallet.name}</span>
                                <div className="ml-auto">
                                    {walletState.walletLoading === wallet._id ? (
                                        <Loader size="small" />
                                    ) : isWalletConnectActive(wallet._id) ? (
                                        <Button name={t('modal.wallet.reconnectButton')} size="small" variant="ghost">
                                            {t('modal.wallet.reconnectButton')}
                                        </Button>
                                    ) : (
                                        <Image asset={wallet.asset} width={24} height={24} isIcon={true} />
                                    )}
                                </div>
                            </button>
                        ),
                )}
            </div>
            {isWalletConnectActive('wallet_connect') && (
                <div className="px-3 py-2 mt-4 rounded-lg ring-1 ring-grey-80 text-grey-30 t__body--b3 md:rounded-xl md:px-4">
                    {t('modal.wallet.walletConnectNote')}
                </div>
            )}
        </>
    );
};
